<template>
  <div
    v-if="currentFormule"
    class="formule-details"
    :class="{ 'formule-details--tooltip': isTooltip }">
    <template v-if="isTooltip">
      <div class="formule-details__header">
        <h5 class="formule-details__title">
          {{ $t('comparator.offers.header.formuleDetails.title', { formuleName: currentFormule.title }) }}
        </h5>
        <button
          type="button"
          class="formule-details__close"
          :aria-label="$t('comparator.offers.header.formuleDetails.close')"
          @click="$emit('close')">
          <font-awesome-icon :icon="['fal', 'times']" />
        </button>
      </div>
      <p class="formule-details__intro">
        {{ $t('comparator.offers.header.formuleDetails.intro') }}
      </p>
    </template>
    <h5
      v-else
      class="formule-details__title">
      {{ $t('comparator.offers.header.formuleDetails.title', { formuleName: currentFormule.title }) }}
    </h5>

    <el-row :gutter="isTooltip ? 16 : 40">
      <el-col
        v-if="currentFormule.details.length"
        :xs="24"
        :sm="12"
        :md="12">
        <div :class="{ 'formule-details__panel': isTooltip }">
          <div
            v-if="isTooltip"
            class="formule-details__panel-header">
            <p class="formule-details__subtitle">
              {{ $t('comparator.offers.header.formuleDetails.success.subTitle') }}
            </p>
            <span class="formule-details__panel-rule formule-details__panel-rule--success" />
          </div>
          <p
            v-else
            class="formule-details__subtitle">
            {{ $t('comparator.offers.header.formuleDetails.success.subTitle') }}
          </p>
          <ul class="formule-details__ul">
            <li
              v-for="detail in currentFormule.details"
              :key="detail"
              class="formule-details__li">
              <div class="formule-details__list">
                <font-awesome-icon
                  :icon="['fal', 'check-circle']"
                  class="formule-details__list__icon formule-details__list__icon--success" />
                <span class="formule-details__list__text">{{ detail }}</span>
              </div>
            </li>
          </ul>
        </div>
      </el-col>
      <el-col
        v-if="currentFormule.left_to_do"
        :xs="24"
        :sm="12"
        :md="12">
        <div :class="{ 'formule-details__panel': isTooltip }">
          <div
            v-if="isTooltip"
            class="formule-details__panel-header">
            <p class="formule-details__subtitle">
              {{ $t('comparator.offers.header.formuleDetails.danger.subTitle') }}
            </p>
            <span class="formule-details__panel-rule formule-details__panel-rule--danger" />
          </div>
          <p
            v-else
            class="formule-details__subtitle">
            {{ $t('comparator.offers.header.formuleDetails.danger.subTitle') }}
          </p>
          <ul class="formule-details__ul">
            <li
              v-for="left_to_do in currentFormule.left_to_do"
              :key="left_to_do"
              class="formule-details__li">
              <div class="formule-details__list">
                <font-awesome-icon
                  :icon="['fal', 'times-circle']"
                  class="formule-details__list__icon formule-details__list__icon--danger" />
                <span class="formule-details__list__text">{{ left_to_do }}</span>
              </div>
            </li>
          </ul>
        </div>
      </el-col>
    </el-row>

    <div
      v-if="isTooltip"
      class="formule-details__footer">
      <div class="formule-details__footer__help">
        <font-awesome-icon
          :icon="['fal', 'lightbulb']"
          class="formule-details__footer__icon" />
        <p class="formule-details__footer__text">
          {{ $t('comparator.offers.header.formuleDetails.help') }}
          <strong>{{ $t('base.phoneNumber') }}</strong>
        </p>
      </div>
      <div class="formule-details__footer__actions">
        <el-button
          type="primary"
          class="formule-details__footer__close-btn"
          @click="$emit('close')">
          {{ $t('comparator.offers.header.formuleDetails.close') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { mapState } from 'pinia';
import { useDemandStore } from '@/stores';
import type { Formule } from '@/types/Offers';

export default {
  name: 'FormuleDetails',
  emits: ['close'],
  props: {
    // Quand fournie, affiche cette formule (ex. une formule proposée en option) ;
    // sinon retombe sur la formule sélectionnée de la demande.
    formule: {
      type: Object as PropType<Formule | null>,
      default: null,
    },
    isTooltip: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(useDemandStore, ['demand']),
    currentFormule(): Formule | undefined {
      return this.formule ?? this.demand?.formule;
    },
  },
};
</script>

<style scoped lang="scss">
.formule-details {
  color: var(--nxt-color-font);
  margin-top: 1.5em;

  &__title {
    margin-bottom: 1.5em;
  }

  &__subtitle {
    font-weight: 700;
    font-size: 15px;
  }

  &__ul {
    font-size: 16px;
    margin: 0;
    padding-left: 0;
    list-style-type: none;
  }

  &__li {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__list {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 8px;
    line-height: 1.4;

    &__icon {
      margin-top: 2px;
      flex-shrink: 0;

      &--danger {
        color: var(--nxt-color-danger);
      }

      &--success {
        color: var(--nxt-color-success);
      }
    }

    &__text {
      flex: 1;
    }
  }

  &--tooltip {
    margin-top: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    min-height: 100%;
    padding: 16px 16px 0;
    overflow-x: hidden;

    .formule-details__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 8px;
    }

    .formule-details__title {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.35;
      color: var(--nxt-color-font);
    }

    .formule-details__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      margin: -4px -4px 0 0;
      padding: 0;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: var(--nxt-color-blue-grey);
      cursor: pointer;
      font-size: 16px;

      &:hover,
      &:focus-visible {
        color: var(--nxt-color-font);
        outline: none;
      }
    }

    .formule-details__intro {
      margin: 0 0 14px;
      font-size: 13px;
      line-height: 1.45;
      color: var(--nxt-color-blue-grey);
    }

    // Padding lives on the parent so el-row gutter negative margins
    // don't overflow the viewport and create a horizontal scrollbar.
    > .el-row {
      flex: 1;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;

      > .el-col {
        margin-bottom: 12px;

        @include breakpoint(tablet) {
          margin-bottom: 0;
        }
      }
    }

    .formule-details__panel {
      height: 100%;
      padding: 14px 12px;
      border: 1px solid var(--nxt-color-grey-border);
      border-radius: 8px;
      background: var(--nxt-color-light-grey);
    }

    .formule-details__panel-header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 12px;
    }

    .formule-details__panel-rule {
      display: block;
      width: 30px;
      height: 2px;
      border-radius: 2px;

      &--success {
        background-color: var(--nxt-color-success);
      }

      &--danger {
        background-color: var(--nxt-color-danger);
      }
    }

    .formule-details__subtitle {
      margin: 0;
      font-size: 13px;
      line-height: 1.3;
    }

    .formule-details__ul {
      font-size: 12px;
    }

    .formule-details__li {
      margin-bottom: 8px;
    }

    .formule-details__list {
      font-size: 12px;

      &__icon {
        font-size: 14px;
      }
    }

    .formule-details__footer {
      display: flex;
      flex-direction: column;
      margin-top: auto;
      padding-top: 16px;
      padding-bottom: 16px;

      @include breakpoint(tablet) {
        padding-top: 24px;
        padding-bottom: 20px;
      }

      &__help {
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }

      &__icon {
        flex-shrink: 0;
        margin-top: 2px;
        color: var(--nxt-color-font);
        font-size: 14px;
      }

      &__text {
        margin: 0;
        font-size: 12px;
        line-height: 1.45;
        color: var(--nxt-color-font);

        strong {
          font-weight: 700;
        }
      }

      &__actions {
        display: flex;
        justify-content: center;
        padding: 16px 0 0;

        @include breakpoint(tablet) {
          display: none;
        }
      }

      &__close-btn {
        min-width: 140px;
      }
    }
  }
}
</style>
